import { z } from 'zod';
/**
 * Every color is a 6-digit hex, matching how `crew-customer-ui` declares its own
 * tokens in `src/theme/colors.css`. Translucency comes from the frosted-glass
 * controls rather than per-color alpha.
 */
const color = z
    .string()
    .toLowerCase()
    .regex(/^#[0-9a-f]{6}$/, 'Enter a 6-digit hex color, e.g. #1a1a1a');
/** A CSS font-family stack, e.g. `'Inter', sans-serif`, or the system stack `system-ui, sans-serif`. */
// only the shape, so front-ends don't bundle the Google Fonts catalog; the API checks the family is on it (`isFontStack`)
const fontFamily = z
    .string()
    .regex(/^(?:'[A-Za-z0-9 ]+', (?:sans-serif|serif|monospace|cursive)|system-ui, sans-serif)$/, 'Use a Google Fonts family or the system font');
export const ShadowLevel = {
    none: 'none',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
};
export const ShadowStyle = {
    standard: 'standard',
    halo: 'halo',
};
export const FontWeight = {
    light: '300',
    regular: '400',
    medium: '500',
    semiBold: '600',
};
/**
 * Fields that support being expanded into a linear gradient. Keyed rather than
 * open-ended so a stop list can't be attached to a field the generator never
 * reads back.
 */
export const GradientField = {
    pageBackground: 'pageBackground',
    buttonPrimaryBg: 'buttonPrimaryBg',
};
/** Extra stops appended after the field's own color, max 3. */
const gradients = z.partialRecord(z.enum(GradientField), z.array(color).max(3));
/**
 * The full theme the generator turns into CSS.
 *
 * Field names are the historical UI vocabulary; the trailing comment on each is
 * the CSS custom property or selector it drives.
 *
 * Only 15 of the color fields are exposed as controls today. The rest are set by
 * preset and still emitted, which is why they all live here.
 */
export const portalTheme = z.object({
    // Brand
    buttonPrimaryBg: color.describe('Brand color: filled buttons, checked checkboxes and radios, field focus rings.'), // --color-brand
    buttonPrimaryText: color.describe('Text and icons on filled buttons.'), // --color-on-brand
    brandContainerColor: color.describe('Soft brand tint behind selected options and chips.'), // --color-brand-container
    onBrandContainerColor: color.describe('Text on the soft brand tint.'), // --color-on-brand-container
    // Plain
    plainColor: color
        .default('#5e5e5e')
        .describe('Secondary (plain) buttons and the button loading spinner.'), // --color-plain
    buttonOutlineText: color.describe('Text of outlined buttons.'), // corso-button[outlined] text
    onPlainColor: color.describe('Text on secondary (plain) buttons.'), // --color-on-plain
    plainContainerColor: color.describe('Soft tint for secondary states.'), // --color-plain-container
    onPlainContainerColor: color.describe('Text on the soft secondary tint.'), // --color-on-plain-container
    buttonOutlineBorder: color.describe('Border of outlined buttons.'), // corso-button[outlined] border
    // Emphasis
    emphasisColor: color.describe('Positive and highlight badges and alerts.'), // --color-emphasis
    onEmphasisColor: color.describe('Text on positive and highlight badges.'), // --color-on-emphasis
    emphasisContainerColor: color.describe('Soft tint behind positive and highlight alerts.'), // --color-emphasis-container
    onEmphasisContainerColor: color.describe('Text in positive and highlight alerts.'), // --color-on-emphasis-container
    // Critical
    criticalColor: color.describe('Errors: error text, invalid field outlines, destructive buttons.'), // --color-critical
    onCriticalColor: color.describe('Text on destructive buttons.'), // --color-on-critical
    criticalContainerColor: color.describe('Soft tint behind error alerts.'), // --color-critical-container
    onCriticalContainerColor: color.describe('Text in error alerts.'), // --color-on-critical-container
    // Surfaces
    surfaceColor: color.describe('The page behind everything; on an embedded portal, glass makes it transparent.'), // --color-surface
    surfaceContainerLowestColor: color.describe('Cards: the inner page card, header, collapsible rows, product popovers.'), // --color-surface-container-lowest
    surfaceContainerLowColor: color.describe('Dropdown lists, the file drop area, summary line-item groups, chips.'), // --color-surface-container-low
    surfaceContainerColor: color.describe('The main content panel around the page card.'), // --color-surface-container
    surfaceContainerHighColor: color.describe('Raised bits: the quantity badge, the pending-button overlay.'), // --color-surface-container-high
    surfaceContainerHighestColor: color.describe('The most raised bits, such as pressed states.'), // --color-surface-container-highest
    // On surface / text
    textPrimaryColor: color.describe('Body text and headings everywhere.'), // --color-on-surface
    textSecondaryColor: color.describe('Muted text: hints, captions, secondary details.'), // --color-on-surface-variant
    brandTextColor: color.describe('Links and brand-colored text accents, and ghost buttons.'), // .text-brand
    // Outline
    inputBorderColor: color.describe('Outlines of inputs, text areas and dropdowns, the dashed file-drop border, the default focus outline.'), // --color-outline
    borderColor: color.describe('Dividers and card and product outlines.'), // --color-outline-variant
    // Inverse & scrim
    inverseSurfaceColor: color.describe('Tooltips and toasts.'), // --color-inverse-surface
    inverseOnSurfaceColor: color.describe('Text in tooltips and toasts.'), // --color-inverse-on-surface
    scrimColor: color.describe('The backdrop behind modals.'), // --color-scrim
    // Typography — each size is the `md` step; the generator derives lg/sm from it
    fontFamily: fontFamily.describe('Body text, labels, buttons and inputs.'), // --font-body-family
    headingFontFamily: fontFamily.describe('Page, card and section titles.'), // --font-heading-family
    headingSize: z
        .number()
        .int()
        .min(16)
        .max(40)
        .describe('Page title size in px; card titles and larger headings scale from it.'),
    subheadingSize: z
        .number()
        .int()
        .min(12)
        .max(24)
        .describe('Section title size in px.'),
    bodySize: z
        .number()
        .int()
        .min(11)
        .max(18)
        .describe('Body text size in px; keep it at 14 or more so hints stay readable on phones.'),
    labelSize: z
        .number()
        .int()
        .min(9)
        .max(16)
        .describe('Button, field-label and line-item title size in px.'),
    fontWeight: z
        .enum(FontWeight)
        .describe('Text weight; medium steps never go below 500.'),
    // Borders & shapes
    borderRadius: z
        .number()
        .int()
        .min(0)
        .max(24)
        .describe('Corner radius in px of the panels, cards, dropdowns and the file drop area.'),
    buttonBorderRadius: z
        .number()
        .int()
        .min(0)
        .max(24)
        .describe('Corner radius in px of buttons; 24 reads as a pill.'),
    fieldBorderRadius: z
        .number()
        .int()
        .min(0)
        .max(24)
        .default(8)
        .describe('Corner radius in px of inputs, text areas and dropdown fields.'),
    cardBorders: z
        .boolean()
        .default(true)
        .describe('Keep the edge around the panels and cards; false removes it.'),
    cardSurfaces: z
        .boolean()
        .default(true)
        .describe('Paint the panel and card backgrounds; false leaves an open, card-free layout sitting on the page.'),
    flatDisclosures: z
        .boolean()
        .default(false)
        .describe('Show collapsible rows (items, tracking) as flat rows split by a hairline instead of filled boxes.'),
    inputBackgroundColor: color.describe('Background of inputs, dropdowns, the file drop area and radio cards.'),
    // Shadows & effects
    shadowLevel: z
        .enum(ShadowLevel)
        .describe('How strongly the panels and cards float; dropdowns sit one step heavier.'),
    shadowStyle: z
        .enum(ShadowStyle)
        .describe('standard is a plain drop shadow; halo adds a hairline ring and a wide soft glow.'),
    backdropBlur: z
        .boolean()
        .describe('Glass: frosted, translucent panels and cards over the page.'),
    glassOpacity: z
        .number()
        .min(0.1)
        .max(1)
        .describe('With glass, how opaque the outer panels and the header are. Lower reads glassier but harder.'),
    glassCardOpacity: z
        .number()
        .min(0.1)
        .max(1)
        .optional()
        .describe('With glass, how opaque the inner page card is, on its own; left out, it sits 0.1 more opaque than the panels. Lower lets the panel show through, as a stacked pane.'),
    glassHighlight: z
        .boolean()
        .default(false)
        .describe('With glass, a thin white highlight along the top edge of the panels and the page card.'),
    // Spacing
    /** Free-form CSS padding shorthand for `corso-page`, e.g. `24px` or `10px 5px 15px 20px`. */
    cardPadding: z
        .string()
        .max(64)
        .describe('Padding inside the page card, as CSS padding: 24px, or 10px 5px 15px 20px.'),
    appBodyPaddingY: z
        .number()
        .int()
        .min(0)
        .max(200)
        .describe('Space in px above and below the portal, on its own URL and embedded in the merchant’s site.'),
    sectionSpacing: z
        .number()
        .int()
        .min(8)
        .max(32)
        .describe('Gap in px between the parts of a page section.'),
    // Page
    pageBackground: color.describe('Page color behind the portal on its own URL; glass leaves the merchant’s page showing when embedded.'),
    pageBackgroundImage: z
        .union([z.literal(''), z.url()])
        .describe('A photo URL to cover the page with, or empty for none.'),
    pageBackgroundFocalX: z
        .number()
        .min(0)
        .max(100)
        .describe('Horizontal focus of the page photo, 0–100%.'),
    pageBackgroundFocalY: z
        .number()
        .min(0)
        .max(100)
        .describe('Vertical focus of the page photo, 0–100%.'),
    gradients: gradients.describe('Up to 3 extra colors that turn pageBackground or buttonPrimaryBg into a diagonal gradient.'),
});
// the portal's own tokens (crew-customer-ui colors.css, typography.css and layout), so Default changes nothing until a field does
export const defaultPortalTheme = {
    buttonPrimaryBg: '#000000',
    buttonPrimaryText: '#e5e1e4',
    brandContainerColor: '#3c3b3d',
    onBrandContainerColor: '#ffffff',
    plainColor: '#5e5e5e',
    buttonOutlineText: '#444749',
    onPlainColor: '#ffffff',
    plainContainerColor: '#d4d4d4',
    onPlainContainerColor: '#1b1b1b',
    buttonOutlineBorder: '#c4c7c9',
    emphasisColor: '#31694b',
    onEmphasisColor: '#ffffff',
    emphasisContainerColor: '#befad3',
    onEmphasisContainerColor: '#1f583b',
    criticalColor: '#ba1a1a',
    onCriticalColor: '#ffffff',
    criticalContainerColor: '#ffdad6',
    onCriticalContainerColor: '#93000a',
    surfaceColor: '#f9f9f9',
    surfaceContainerLowestColor: '#ffffff',
    surfaceContainerLowColor: '#f3f3f3',
    surfaceContainerColor: '#eeeeee',
    surfaceContainerHighColor: '#e8e8e8',
    surfaceContainerHighestColor: '#e2e2e2',
    textPrimaryColor: '#1a1c1c',
    textSecondaryColor: '#444749',
    brandTextColor: '#000000',
    inputBorderColor: '#747779',
    borderColor: '#c4c7c9',
    inverseSurfaceColor: '#2f3131',
    inverseOnSurfaceColor: '#f0f1f1',
    scrimColor: '#000000',
    fontFamily: "'Inter', sans-serif",
    headingFontFamily: "'Inter', sans-serif",
    headingSize: 24,
    subheadingSize: 16,
    bodySize: 14,
    labelSize: 12,
    fontWeight: FontWeight.regular,
    borderRadius: 8,
    buttonBorderRadius: 8,
    fieldBorderRadius: 8,
    cardBorders: true,
    cardSurfaces: true,
    flatDisclosures: false,
    inputBackgroundColor: '#ffffff',
    shadowLevel: ShadowLevel.sm,
    shadowStyle: ShadowStyle.standard,
    backdropBlur: false,
    glassOpacity: 1,
    glassHighlight: false,
    cardPadding: '24px',
    // the portal's own `p-2` until set
    appBodyPaddingY: 0,
    sectionSpacing: 16,
    pageBackground: '#f9f9f9',
    pageBackgroundImage: '',
    pageBackgroundFocalX: 50,
    pageBackgroundFocalY: 50,
    gradients: {},
};
export const PortalThemePreset = {
    default: 'Default',
    glass: 'Glass',
    frosted: 'Frosted',
    compact: 'Compact',
    editorial: 'Editorial',
    pill: 'Pill',
    dark: 'Dark',
    ladyBird: 'Lady Bird',
};
export const portalThemePresets = {
    [PortalThemePreset.default]: {
        description: 'The portal as it ships: clean, Inter, light grey page',
        theme: defaultPortalTheme,
    },
    [PortalThemePreset.glass]: {
        description: 'Frosted blur, translucent layers',
        // tuned against a real merchant photo; the opacities only pass with these darker text and outline colors
        theme: {
            ...defaultPortalTheme,
            buttonPrimaryBg: '#111111',
            buttonPrimaryText: '#ffffff',
            brandContainerColor: '#f4f4f4',
            onBrandContainerColor: '#1a1a1a',
            brandTextColor: '#111111',
            buttonOutlineBorder: '#111111',
            buttonOutlineText: '#111111',
            surfaceColor: '#eef0f3',
            surfaceContainerLowestColor: '#ffffff',
            surfaceContainerLowColor: '#ffffff',
            surfaceContainerColor: '#ffffff',
            // darker than the defaults so text clears AA over a busy photo at these opacities
            textPrimaryColor: '#111111',
            textSecondaryColor: '#3a3a3a',
            inputBorderColor: '#7a7a7a',
            borderColor: '#d6d6d6',
            criticalColor: '#b91c1c',
            inputBackgroundColor: '#ffffff',
            borderRadius: 20,
            buttonBorderRadius: 12,
            shadowLevel: ShadowLevel.md,
            backdropBlur: true,
            glassOpacity: 0.65,
            glassHighlight: true,
            pageBackground: '#eef0f3',
        },
    },
    // the presets below come from the team's Lovable style studio, mapped onto these controls
    [PortalThemePreset.frosted]: {
        description: 'Soft, business: subtle frosted panels on a cool grey page',
        theme: {
            ...defaultPortalTheme,
            surfaceContainerLowestColor: '#ffffff',
            surfaceContainerColor: '#ffffff',
            backdropBlur: true,
            glassOpacity: 0.85,
            pageBackground: '#eef1f6',
            surfaceColor: '#eef1f6',
            shadowLevel: ShadowLevel.md,
            borderRadius: 20,
            buttonBorderRadius: 14,
            fieldBorderRadius: 14,
        },
    },
    [PortalThemePreset.compact]: {
        description: 'Tight and sharp: small corners, Space Grotesk, no shadow',
        theme: {
            ...defaultPortalTheme,
            fontFamily: "'Space Grotesk', sans-serif",
            headingFontFamily: "'Space Grotesk', sans-serif",
            headingSize: 20,
            borderRadius: 6,
            buttonBorderRadius: 6,
            fieldBorderRadius: 4,
            shadowLevel: ShadowLevel.none,
            cardPadding: '16px',
            sectionSpacing: 12,
        },
    },
    [PortalThemePreset.editorial]: {
        description: 'Large type and airy: DM Serif Display headings, DM Sans text',
        theme: {
            ...defaultPortalTheme,
            fontFamily: "'DM Sans', sans-serif",
            headingFontFamily: "'DM Serif Display', serif",
            headingSize: 32,
            subheadingSize: 18,
            bodySize: 16,
            borderRadius: 18,
            buttonBorderRadius: 18,
            fieldBorderRadius: 12,
            shadowLevel: ShadowLevel.md,
            cardPadding: '32px',
            sectionSpacing: 24,
            appBodyPaddingY: 40,
        },
    },
    [PortalThemePreset.pill]: {
        description: 'Fully rounded: pill buttons and fields, soft cards, Nunito',
        theme: {
            ...defaultPortalTheme,
            fontFamily: "'Nunito', sans-serif",
            headingFontFamily: "'Nunito', sans-serif",
            borderRadius: 24,
            buttonBorderRadius: 24,
            fieldBorderRadius: 24,
            cardPadding: '28px',
        },
    },
    [PortalThemePreset.dark]: {
        description: 'Dark indigo surfaces with a periwinkle brand',
        theme: {
            ...defaultPortalTheme,
            buttonPrimaryBg: '#818cf8',
            buttonPrimaryText: '#0f0f1a',
            brandContainerColor: '#2e2e48',
            onBrandContainerColor: '#e4e4ef',
            brandTextColor: '#818cf8',
            buttonOutlineText: '#e4e4ef',
            buttonOutlineBorder: '#6b7280',
            emphasisColor: '#60a5fa',
            onEmphasisColor: '#0f0f1a',
            emphasisContainerColor: '#1e2a4a',
            onEmphasisContainerColor: '#bfdbfe',
            criticalColor: '#f87171',
            onCriticalColor: '#1a0505',
            criticalContainerColor: '#3b1219',
            onCriticalContainerColor: '#fecaca',
            surfaceColor: '#1e1e2e',
            surfaceContainerLowestColor: '#141422',
            surfaceContainerLowColor: '#232338',
            surfaceContainerColor: '#282840',
            surfaceContainerHighColor: '#2e2e48',
            surfaceContainerHighestColor: '#343450',
            textPrimaryColor: '#e4e4ef',
            textSecondaryColor: '#9ca3af',
            inputBorderColor: '#6b7280',
            borderColor: '#374151',
            inputBackgroundColor: '#1e1e2e',
            pageBackground: '#141422',
            shadowLevel: ShadowLevel.md,
        },
    },
    [PortalThemePreset.ladyBird]: {
        description: 'Card-free and open: navy on white, pill buttons, no panels, borders or shadows',
        theme: {
            ...defaultPortalTheme,
            buttonPrimaryBg: '#051941',
            buttonPrimaryText: '#ffffff',
            brandTextColor: '#051941',
            buttonOutlineText: '#051941',
            buttonOutlineBorder: '#051941',
            textPrimaryColor: '#051941',
            textSecondaryColor: '#051941',
            inputBorderColor: '#051941',
            borderColor: '#051941',
            surfaceColor: '#ffffff',
            pageBackground: '#ffffff',
            borderRadius: 0,
            buttonBorderRadius: 24,
            fieldBorderRadius: 15,
            cardBorders: false,
            cardSurfaces: false,
            shadowLevel: ShadowLevel.none,
        },
    },
};
//# sourceMappingURL=portalTheme.js.map